// AddTimetableEntryModal.tsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import useClassesStore from '../../../stateManagers/store/useClassesStore';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalChipSelector, ChipOption } from '../../../components/GlobalComponents/GlobalChipSelector';
import useTimetableTypes from '../../../stateManagers/hooks/useTimetableTypes';
import { useTimeSlots } from '../../../stateManagers/hooks/useTimeSlots';
import { useSubjectsByClass } from '../../../stateManagers/hooks/useSubjectsByClass';
import { modalStyles } from './subparts/modalStyles';
import TimetableEntryCard from './subparts/TimetableEntryCard';
import { AddTimetableEntryModalProps, TimetableEntryFormData } from './subparts/types';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { hasNoSubject, hasNoTeacher } from './subparts/timetableSlotTypes';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';

/* ── Assigned-teachers sidebar types ─────────────────────────────── */

interface SideAssignment {
  id: string;
  sections: string[];
  teacher_name: string | null;
  subject_name: string;
}

interface SideSubjectGroup {
  subjectName: string;
  assignments: SideAssignment[];
  assignedCount: number;
}

const SIDEBAR_MAX_CHIPS = 5;

const AddTimetableEntryModal: React.FC<AddTimetableEntryModalProps> = ({
  open,
  onClose,
  onSuccess,
  context,
}) => {
  const toast = useRef<Toast>(null);

  const [formData, setFormData] = useState<TimetableEntryFormData>({
    timetableTypeId: '',
    classId: '',
    section: null,
    entries: [{
      id: crypto.randomUUID(),
      timeSlotId: '',
      subjectId: '',
      teacherAssignmentId: '',
      daysOfWeek: [],
      roomNumber: '',
    }],
  });

  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // ── Assigned-teachers sidebar state ──────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sideAssignments, setSideAssignments] = useState<SideAssignment[]>([]);
  const [sideLoading, setSideLoading] = useState(false);
  const [sideError, setSideError] = useState<string | null>(null);
  const [sectionOnly, setSectionOnly] = useState(true);
  const sideRequestId = useRef(0);

  const { classOptions, fetchClasses, isFetching } = useClassesStore();
  const timetableTypes = useTimetableTypes(true);

  // ── Session context ──────────────────────────────────────────────
  const schoolDetails = useSchoolDetailsStore((s) => s.schoolDetails);
  const sessionId = schoolDetails?.current_session?.id ?? null;
  const sessionLabel = schoolDetails?.current_session?.session ?? null;

  const selectedClass = classOptions.find((c) => c.id === formData.classId);
  const selectedClassSections = selectedClass?.sections ?? [];
  const hasSections = selectedClassSections.length > 0;

  const classIdForSubjects = formData.classId || null;
  const { subjectOptions, loading: subjectsLoading, error: subjectsError } = useSubjectsByClass(classIdForSubjects, true);

  const hasTimetableType = !!formData.timetableTypeId || !!context?.timetableTypeId;
  const hasClass = !!formData.classId;
  const hasSectionIfNeeded = !hasSections || !!formData.section;
  const canShowEntries = hasTimetableType && hasClass && hasSectionIfNeeded;

  const timetableTypeIdForHook = canShowEntries
    ? formData.timetableTypeId || context?.timetableTypeId || ''
    : null;

  const { timeSlots, loading: timeSlotsLoading, error: timeSlotsError } = useTimeSlots(timetableTypeIdForHook);

  const hasExistingEntries = context?.existingItems && context.existingItems.length > 0;

  useEffect(() => {
    if (open) fetchClasses(true);
  }, [open, fetchClasses]);

  useEffect(() => {
    if (open) {
      // Sidebar always starts collapsed when the modal is (re)opened
      setSidebarOpen(false);
      setSectionOnly(true);

      if (context) {
        setFormData({
          timetableTypeId: context.timetableTypeId,
          classId: context.classId,
          section: context.section,
          entries: [{
            id: crypto.randomUUID(),
            timeSlotId: '',
            subjectId: '',
            teacherAssignmentId: '',
            daysOfWeek: [],
            roomNumber: '',
          }],
        });
      } else {
        setFormData({
          timetableTypeId: '',
          classId: '',
          section: null,
          entries: [{
            id: crypto.randomUUID(),
            timeSlotId: '',
            subjectId: '',
            teacherAssignmentId: '',
            daysOfWeek: [],
            roomNumber: '',
          }],
        });
      }
    }
  }, [open, context]);

  useEffect(() => {
    if (context?.periodLabel && timeSlots.length > 0 && formData.entries.length === 1) {
      const match = timeSlots.find((slot) =>
        slot.label.toLowerCase().includes(context.periodLabel.toLowerCase())
      );
      if (match) {
        setFormData((prev) => ({
          ...prev,
          entries: [{ ...prev.entries[0], timeSlotId: match.id }],
        }));
      }
    }
  }, [timeSlots, context?.periodLabel]);

  /* ══════════════════════════════════════════════════════════════════
     ASSIGNED TEACHERS SIDEBAR
     - Same endpoint the Assigned Teachers dashboard uses
     - Scoped to the current session
     - Only fetched while the sidebar is open and a class is selected
     - Stale responses are discarded via sideRequestId
  ══════════════════════════════════════════════════════════════════ */

  const fetchSideAssignments = useCallback(async () => {
    const classId = formData.classId;
    if (!classId) {
      setSideAssignments([]);
      setSideError(null);
      return;
    }
    if (!sessionId) {
      setSideAssignments([]);
      setSideError('No active session found.');
      return;
    }

    const requestId = ++sideRequestId.current;
    setSideLoading(true);
    setSideError(null);
    try {
      const res = await apiClient.get(`/api/assigned-teachers/class/${classId}`, {
        params: { session_id: sessionId },
      });

      if (requestId !== sideRequestId.current) return;

      if (res.data.status === 'success') {
        const data: SideAssignment[] = (res.data.assignments || []).map((a: any) => ({
          id: String(a.id || a._id || a.ID || ''),
          sections: a.sections ?? [],
          teacher_name: a.teacher_name || null,
          subject_name: a.subject_name || '',
        }));
        setSideAssignments(data);
      } else {
        setSideAssignments([]);
        setSideError(res.data.message || 'No assignments found.');
      }
    } catch (err: any) {
      if (requestId !== sideRequestId.current) return;
      setSideAssignments([]);
      setSideError(err.response?.data?.message || 'Failed to load assigned teachers.');
    } finally {
      if (requestId === sideRequestId.current) setSideLoading(false);
    }
  }, [formData.classId, sessionId]);

  useEffect(() => {
    if (open && sidebarOpen) {
      fetchSideAssignments();
    } else {
      // invalidate any in-flight request
      sideRequestId.current++;
      setSideLoading(false);
    }
  }, [open, sidebarOpen, fetchSideAssignments]);

  const sideGroups: SideSubjectGroup[] = useMemo(() => {
    const activeSection = sectionOnly ? formData.section : null;
    const source = activeSection
      ? sideAssignments.filter((a) => (a.sections ?? []).includes(activeSection))
      : sideAssignments;

    const map: Record<string, SideAssignment[]> = {};
    source.forEach((a) => {
      if (!map[a.subject_name]) map[a.subject_name] = [];
      map[a.subject_name].push(a);
    });

    return Object.entries(map)
      .map(([subjectName, assignments]) => ({
        subjectName,
        assignments,
        assignedCount: assignments.filter((a) => a.teacher_name).length,
      }))
      .sort((a, b) => a.subjectName.localeCompare(b.subjectName));
  }, [sideAssignments, sectionOnly, formData.section]);

  const sideAssignedSubjects = sideGroups.filter((g) => g.assignedCount > 0).length;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleAddEntry = () => {
    setFormData((prev) => ({
      ...prev,
      entries: [...prev.entries, {
        id: crypto.randomUUID(),
        timeSlotId: '',
        subjectId: '',
        teacherAssignmentId: '',
        daysOfWeek: [],
        roomNumber: '',
      }],
    }));
  };

  const handleRemoveEntry = (id: string) => {
    if (formData.entries.length === 1) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'At least one entry is required',
        life: 3000,
      });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.filter((entry) => entry.id !== id),
    }));
  };

  const handleUpdateEntry = (
    id: string,
    field: 'timeSlotId' | 'subjectId' | 'teacherAssignmentId' | 'roomNumber',
    value: string
  ) => {
    // Duplicate subject guard
    if (field === 'subjectId' && value) {
      const isDuplicate = formData.entries.some(
        (entry) => entry.id !== id && entry.subjectId === value
      );
      if (isDuplicate) {
        const selectedSubject = subjectOptions.find((sub) => sub.value === value);
        toast.current?.show({
          severity: 'error',
          summary: 'Duplicate Subject',
          detail: `Subject "${selectedSubject?.label || value}" is already selected in another entry`,
          life: 4000,
        });
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.map((entry) => {
        if (entry.id !== id) return entry;
        const updated = { ...entry, [field]: value };

        // Clear subject for all non-teaching slots; only clear teacher for break (optional for others)
        if (field === 'timeSlotId') {
          const newSlotType = timeSlots.find((s) => s.id === value)?.slotType;
          if (hasNoSubject(newSlotType)) {
            updated.subjectId = '';
          }
          if (hasNoTeacher(newSlotType)) {
            updated.teacherAssignmentId = '';
          }
        }

        return updated;
      }),
    }));
  };

  const handleUpdateDays = (id: string, days: string[]) => {
    setFormData((prev) => ({
      ...prev,
      entries: prev.entries.map((entry) =>
        entry.id === id ? { ...entry, daysOfWeek: days } : entry
      ),
    }));
  };

  const getOccupiedDaysForSlot = (currentEntryId: string, timeSlotId: string): string[] => {
    return formData.entries
      .filter((entry) => entry.id !== currentEntryId && entry.timeSlotId === timeSlotId)
      .flatMap((entry) => entry.daysOfWeek);
  };

  /* ── Chip options (Timetable Type / Class / Section) ─────────────── */

  const timetableTypeChipOptions = timetableTypes
  .filter((t) => t.id && !t.label.toLowerCase().startsWith('select'))
  .map((t) => ({
    value: t.id,
    label: t.label,
  }));

  const classChipOptions = classOptions.map((c) => ({
    value: c.id,
    label: c.label,
  }));

  const sectionChipOptions = selectedClassSections.map((s) => ({
    value: s,
    label: `Section ${s}`,
  }));

  /* ── Chip options (Subject / Time Slot — used by TimetableEntryCard) ── */

  const timeSlotChipOptions: ChipOption[] = timeSlots.map((slot) => ({
    value: slot.id,
    label: slot.label,
  }));

  const subjectChipOptions: ChipOption[] = subjectOptions.map((sub) => ({
    value: sub.value,
    label: sub.label,
  }));

  /* ══════════════════════════════════════════════════════════════════
     SUBMIT
     - teacherAssignmentId holds the real teacher_id (set by card)
     - session_id / session pulled from useSchoolDetailsStore.current_session
     - One POST per entry fired in parallel via Promise.all
     - Slot type rules:
       - 'break'                              -> no subject, no teacher
       - 'power_nap' / 'workbook' / 'activities' -> no subject, teacher OPTIONAL
       - everything else (class, lab, assembly)   -> subject + teacher REQUIRED
  ══════════════════════════════════════════════════════════════════ */
  const handleSubmit = async () => {
    // Session validation — must resolve before hitting the backend
    if (!sessionId || !sessionLabel) {
      toast.current?.show({
        severity: 'error',
        summary: 'Session Error',
        detail: 'No active session found. Please reload the page and try again.',
        life: 5000,
      });
      return;
    }

    // Top-level validation
    if (!formData.timetableTypeId || !formData.classId) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select timetable type and class',
        life: 5000,
      });
      return;
    }

    if (hasSections && !formData.section) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select a section',
        life: 5000,
      });
      return;
    }

    // Per-entry validation + day-overlap check
    const slotDayMap = new Map<string, string[]>();

    for (const entry of formData.entries) {
      const entrySlotType = timeSlots.find((s) => s.id === entry.timeSlotId)?.slotType;
      const noSubjectEntry = hasNoSubject(entrySlotType);
      const teacherRequired = !noSubjectEntry; // only required for teaching slots

      if (
        !entry.timeSlotId ||
        (!noSubjectEntry && !entry.subjectId) ||
        (teacherRequired && !entry.teacherAssignmentId) ||
        entry.daysOfWeek.length === 0
      ) {
        toast.current?.show({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please complete all fields and select at least one day for each entry',
          life: 5000,
        });
        return;
      }

      const existingDays = slotDayMap.get(entry.timeSlotId) || [];
      const overlap = entry.daysOfWeek.some((day) => existingDays.includes(day));
      if (overlap) {
        const slotLabel =
          timeSlots.find((s) => s.id === entry.timeSlotId)?.label || entry.timeSlotId;
        toast.current?.show({
          severity: 'error',
          summary: 'Day Overlap',
          detail: `Time slot "${slotLabel}" has overlapping days. Each day can only be used once per period.`,
          life: 6000,
        });
        return;
      }
      slotDayMap.set(entry.timeSlotId, [...existingDays, ...entry.daysOfWeek]);
    }

    // Build one payload per entry
    // NOTE: all IDs are Mongo ObjectID hex strings — do NOT parseInt() them.
    const payloads = formData.entries.map((entry) => {
      const entrySlotType = timeSlots.find((s) => s.id === entry.timeSlotId)?.slotType;
      const noSubjectEntry = hasNoSubject(entrySlotType);
      return {
        session_id: sessionId,
        session: sessionLabel,
        class_id: formData.classId,
        section: hasSections ? formData.section : null,
        subject_id: noSubjectEntry ? null : entry.subjectId,
        teacher_id: entry.teacherAssignmentId || null,
        timetable_type_id: formData.timetableTypeId,
        time_slot_id: entry.timeSlotId,
        day_of_week: entry.daysOfWeek,
        room_number: entry.roomNumber || null,
      };
    });

    setLoading(true);
    try {
      const responses = await Promise.all(
        payloads.map((payload) => apiClient.post('/api/timetable', payload))
      );

      const failed = responses.filter((res) => res.data.status !== 'success');
      const successCount = responses.length - failed.length;

      if (failed.length === 0) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `${successCount} timetable ${successCount === 1 ? 'entry' : 'entries'} added successfully!`,
          life: 4000,
        });
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 300);
      } else if (successCount > 0) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Partial Success',
          detail: `${successCount} succeeded, ${failed.length} failed. — ${failed[0]?.data?.message || 'Unknown error'}`,
          life: 5000,
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: failed[0]?.data?.message || 'All entries failed to save.',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to add entries',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ── Header ───────────────────────────────────────────────────── */

  const currentTimetableTypeLabel =
    timetableTypes.find((t) => t.id === formData.timetableTypeId)?.label ||
    (context?.timetableTypeId
      ? timetableTypes.find((t) => t.id === context.timetableTypeId)?.label
      : null);

  const currentClassLabel =
    classOptions.find((c) => c.id === formData.classId)?.label ||
    (context?.classId
      ? classOptions.find((c) => c.id === context.classId)?.label
      : null);

  const currentSectionLabel =
    formData.section || context?.section
      ? `Section ${formData.section || context?.section}`
      : null;

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0', width: '100%' }}>
      <div style={{
        width: '54px', height: '54px', borderRadius: '14px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontSize: '24px', fontWeight: '700',
        boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
        flexShrink: 0, border: '3px solid white',
      }}>
        <i className={hasExistingEntries ? 'pi pi-calendar-times' : 'pi pi-plus-circle'} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af', marginBottom: '4px' }}>
          {hasExistingEntries
            ? `Manage Entries (${context?.existingItems.length})`
            : currentTimetableTypeLabel || 'Add Timetable Entry'}
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
          {context ? (
            <>
              {context.day === 'All' ? 'All days' : context.day} • {context.periodLabel}
              {currentClassLabel && (
                <>
                  {' • '}<strong>{currentClassLabel}</strong>
                  {currentSectionLabel && ` ${currentSectionLabel}`}
                </>
              )}
            </>
          ) : currentTimetableTypeLabel ? (
            currentClassLabel ? (
              <>
                <strong>{currentClassLabel}</strong>
                {currentSectionLabel && ` ${currentSectionLabel}`}
              </>
            ) : 'Select class and section below'
          ) : 'Select timetable type, class, and section'}
        </div>
      </div>

      {/* Sidebar toggle */}
      <button
        type="button"
        className={`tt-side-toggle${sidebarOpen ? ' active' : ''}`}
        onClick={() => setSidebarOpen((v) => !v)}
        title={sidebarOpen ? 'Hide assigned subjects' : 'View assigned subjects for this class'}
      >
        <i className={sidebarOpen ? 'pi pi-angle-double-right' : 'pi pi-users'} />
        <span className="tt-side-toggle-label">
          {sidebarOpen ? 'Hide Assigned' : 'Assigned Subjects'}
        </span>
      </button>
    </div>
  );

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.timetable-entry-modal .p-dialog');
      const mask = document.querySelector('.timetable-entry-modal');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  /* ── Assigned subjects sidebar (right) ───────────────────────────── */

  const renderSidebarBody = () => {
    if (!formData.classId) {
      return (
        <div className="tt-side-state">
          <i className="pi pi-filter" />
          <div className="tt-side-state-title">No class selected</div>
          <div className="tt-side-state-text">Select a class to see the subjects assigned to it.</div>
        </div>
      );
    }

    if (sideLoading && sideAssignments.length === 0) {
      return (
        <div className="tt-side-list">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="tt-side-card" style={{ padding: 12 }}>
              <div className="tt-shimmer" style={{ height: 14, width: '55%', borderRadius: 6, marginBottom: 10 }} />
              <div className="tt-shimmer" style={{ height: 12, borderRadius: 6, marginBottom: 6 }} />
              <div className="tt-shimmer" style={{ height: 12, width: '80%', borderRadius: 6 }} />
            </div>
          ))}
        </div>
      );
    }

    if (sideError) {
      return (
        <div className="tt-side-state">
          <i className="pi pi-exclamation-triangle" style={{ color: '#d97706' }} />
          <div className="tt-side-state-title">Couldn't load assignments</div>
          <div className="tt-side-state-text">{sideError}</div>
        </div>
      );
    }

    if (sideGroups.length === 0) {
      return (
        <div className="tt-side-state">
          <i className="pi pi-inbox" />
          <div className="tt-side-state-title">No assignments found</div>
          <div className="tt-side-state-text">
            {sectionOnly && formData.section
              ? `No teachers are assigned for Section ${formData.section}. Show all sections or assign teachers from the entry form.`
              : 'No teachers are assigned to this class yet.'}
          </div>
        </div>
      );
    }

    return (
      <div className="tt-side-list" style={{ opacity: sideLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {sideGroups.map((g) => {
          const ratio = g.assignments.length > 0 ? g.assignedCount / g.assignments.length : 0;
          const pill =
            ratio === 1
              ? { bg: '#d1fae5', color: '#065f46', dot: '#10b981', label: 'Complete' }
              : ratio === 0
                ? { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444', label: 'Pending' }
                : { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', label: `${g.assignedCount}/${g.assignments.length}` };

          return (
            <div key={g.subjectName} className="tt-side-card">
              <div className="tt-side-card-head">
                <div className="tt-side-subject-icon"><i className="pi pi-book" /></div>
                <div className="tt-side-subject-name" title={g.subjectName}>{g.subjectName}</div>
                <span className="tt-side-pill" style={{ background: pill.bg, color: pill.color }}>
                  <span className="tt-side-dot" style={{ background: pill.dot }} />
                  {pill.label}
                </span>
              </div>

              <div className="tt-side-rows">
                {g.assignments.map((a) => {
                  const assigned = !!a.teacher_name;
                  const activeSection = sectionOnly ? formData.section : null;
                  const sections = (a.sections ?? []).filter((s) => !activeSection || s === activeSection);
                  const visible = sections.slice(0, SIDEBAR_MAX_CHIPS);
                  const extra = sections.length - visible.length;
                  return (
                    <div key={a.id} className="tt-side-row">
                      <div
                        className="tt-side-avatar"
                        style={{ background: assigned ? '#dcfce7' : '#fef3c7' }}
                      >
                        <i
                          className={assigned ? 'pi pi-user' : 'pi pi-user-minus'}
                          style={{ color: assigned ? '#166534' : '#b45309' }}
                        />
                      </div>
                      <div
                        className="tt-side-teacher"
                        title={a.teacher_name || 'Not assigned'}
                        style={{ color: assigned ? '#1f2937' : '#d97706' }}
                      >
                        {a.teacher_name || 'Not assigned'}
                      </div>
                      <div className="tt-side-chips" title={sections.join(', ')}>
                        {sections.length === 0 && <span style={{ fontSize: 10.5, color: '#9ca3af' }}>—</span>}
                        {visible.map((sec) => (
                          <span
                            key={sec}
                            className={`tt-side-chip${formData.section === sec ? ' current' : ''}`}
                          >
                            {sec}
                          </span>
                        ))}
                        {extra > 0 && <span className="tt-side-chip muted">+{extra}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSidebar = () => (
    <aside className="tt-side">
      <div className="tt-side-header">
        <div className="tt-side-title-wrap">
          <div className="tt-side-title">Assigned Subjects</div>
          <div className="tt-side-subtitle">
            {formData.classId
              ? `${currentClassLabel ?? 'Class'}${
                  sectionOnly && formData.section ? ` • Section ${formData.section}` : ''
                } • ${sideAssignedSubjects}/${sideGroups.length} subjects assigned`
              : 'Pick a class to view'}
          </div>
        </div>
        <button
          type="button"
          className="tt-side-refresh"
          onClick={fetchSideAssignments}
          disabled={sideLoading || !formData.classId}
          title="Refresh assigned subjects"
        >
          <i className={sideLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'} />
        </button>
      </div>

      {formData.classId && formData.section && (
        <div className="tt-side-scope">
          <button
            type="button"
            className={`tt-side-scope-btn${sectionOnly ? ' active' : ''}`}
            onClick={() => setSectionOnly(true)}
          >
            Section {formData.section}
          </button>
          <button
            type="button"
            className={`tt-side-scope-btn${!sectionOnly ? ' active' : ''}`}
            onClick={() => setSectionOnly(false)}
          >
            All sections
          </button>
        </div>
      )}

      <div className="tt-side-body">{renderSidebarBody()}</div>
    </aside>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{
          width: '92vw',
          maxWidth: sidebarOpen ? 'min(1440px, 96vw)' : '1050px',
          borderRadius: '24px',
          overflow: 'hidden',
          border: '1.5px solid rgba(59, 130, 246, 0.35)',
          boxSizing: 'border-box',
        }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
        maskClassName="timetable-entry-modal"
      >
        <style>{modalStyles}</style>
        <style>{`
          .timetable-entry-modal .p-dialog { transition: max-width 0.3s ease; }
          .timetable-entry-modal .p-dialog-header { padding-right: 16px; }

          /* ── Two-pane layout ── */
          .tt-split { display: flex; gap: 16px; align-items: flex-start; }
          .tt-main { flex: 1 1 auto; min-width: 0; }

          /* ── Header toggle ── */
          .tt-side-toggle {
            display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0;
            padding: 9px 14px; border-radius: 10px; cursor: pointer; outline: none;
            border: 1.5px solid rgba(59,130,246,0.35); background: rgba(59,130,246,0.08);
            color: #1e40af; font-size: 13px; font-weight: 600; transition: all 0.2s ease;
          }
          .tt-side-toggle:hover { background: rgba(59,130,246,0.16); }
          .tt-side-toggle.active {
            background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
            border-color: #3b82f6; color: #fff; box-shadow: 0 4px 12px rgba(59,130,246,0.3);
          }
          @media (max-width: 640px) { .tt-side-toggle-label { display: none; } }

          /* ── Sidebar ── */
          .tt-side {
            width: 360px; flex-shrink: 0; position: sticky; top: 0;
            display: flex; flex-direction: column;
            max-height: calc(90vh - 160px);
            background: rgba(255,255,255,0.9); border-radius: 16px;
            border: 1px solid rgba(59,130,246,0.15);
            box-shadow: 0 4px 16px rgba(0,0,0,0.05); overflow: hidden;
            animation: ttSideIn 0.3s ease;
          }
          @keyframes ttSideIn { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: translateX(0); } }

          .tt-side-header {
            display: flex; align-items: center; gap: 10px; padding: 14px 14px 12px;
            border-bottom: 1px solid rgba(229,231,235,0.7);
          }
          .tt-side-title-wrap { flex: 1; min-width: 0; }
          .tt-side-title { font-size: 14px; font-weight: 700; color: #1e3a8a; }
          .tt-side-subtitle {
            font-size: 11.5px; color: #64748b; font-weight: 500; margin-top: 2px;
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          }
          .tt-side-refresh {
            width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0; cursor: pointer;
            display: flex; align-items: center; justify-content: center; outline: none;
            border: 1px solid rgba(59,130,246,0.3); background: rgba(59,130,246,0.08);
            color: #2563eb; transition: all 0.2s ease;
          }
          .tt-side-refresh:hover:not(:disabled) { background: rgba(59,130,246,0.18); }
          .tt-side-refresh:disabled { opacity: 0.55; cursor: not-allowed; }
          .tt-side-refresh i { font-size: 13px; }

          .tt-side-scope { display: flex; gap: 6px; padding: 10px 14px 0; }
          .tt-side-scope-btn {
            padding: 5px 12px; border-radius: 8px; border: 1.5px solid #e5e7eb; background: #fff;
            color: #475569; font-size: 12px; font-weight: 600; cursor: pointer; outline: none;
            transition: all 0.2s ease;
          }
          .tt-side-scope-btn:hover:not(.active) { background: #f0f9ff; }
          .tt-side-scope-btn.active {
            border-color: #3b82f6; color: #fff;
            background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
          }

          .tt-side-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 12px 14px 14px; }
          .tt-side-list { display: flex; flex-direction: column; gap: 10px; }

          .tt-side-card {
            background: #fff; border-radius: 12px; border: 1px solid #e5e7eb; overflow: hidden;
          }
          .tt-side-card-head { display: flex; align-items: center; gap: 8px; padding: 10px 12px 8px; }
          .tt-side-subject-icon {
            width: 26px; height: 26px; border-radius: 7px; flex-shrink: 0;
            background: linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%);
            display: flex; align-items: center; justify-content: center; color: #fff; font-size: 11px;
          }
          .tt-side-subject-name {
            flex: 1; min-width: 0; font-size: 13px; font-weight: 700; color: #1f2937;
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          }
          .tt-side-pill {
            display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0;
            padding: 2px 9px; border-radius: 20px; font-size: 10.5px; font-weight: 700; white-space: nowrap;
          }
          .tt-side-dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }

          .tt-side-rows { padding: 0 0 4px; }
          .tt-side-row {
            display: flex; align-items: center; gap: 8px; padding: 6px 12px;
            border-top: 1px dashed #f3f4f6;
          }
          .tt-side-avatar {
            width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
            display: flex; align-items: center; justify-content: center;
          }
          .tt-side-avatar i { font-size: 10px; }
          .tt-side-teacher {
            flex: 1; min-width: 0; font-size: 12.5px; font-weight: 600;
            white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          }
          .tt-side-chips { display: flex; gap: 3px; flex-shrink: 0; align-items: center; }
          .tt-side-chip {
            display: inline-flex; align-items: center; padding: 1px 7px; border-radius: 20px;
            background: #eff6ff; color: #1e40af; font-size: 10.5px; font-weight: 700;
            border: 1px solid #bfdbfe; line-height: 1.5;
          }
          .tt-side-chip.current { background: #1e40af; color: #fff; border-color: #1e40af; }
          .tt-side-chip.muted { background: #f3f4f6; color: #6b7280; border-color: #e5e7eb; }

          .tt-side-state {
            display: flex; flex-direction: column; align-items: center; text-align: center;
            padding: 36px 12px; gap: 6px;
          }
          .tt-side-state > i { font-size: 28px; color: #cbd5e1; margin-bottom: 6px; }
          .tt-side-state-title { font-size: 14px; font-weight: 700; color: #374151; }
          .tt-side-state-text { font-size: 12.5px; color: #94a3b8; line-height: 1.5; max-width: 260px; }

          .tt-shimmer {
            background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%);
            background-size: 600px 100%; animation: ttShimmer 1.2s infinite linear;
          }
          @keyframes ttShimmer { 0% { background-position: -300px 0; } 100% { background-position: 300px 0; } }

          /* ── Narrow screens: stack the sidebar under the form ── */
          @media (max-width: 960px) {
            .tt-split { flex-direction: column; }
            .tt-main { width: 100%; }
            .tt-side { width: 100%; position: static; max-height: 420px; }
          }
        `}</style>

        <div className="tt-split">
          {/* ═════════ LEFT: form ═════════ */}
          <div className="tt-main">
            {hasExistingEntries && context && (
              <div style={{ marginBottom: '24px' }}>
                <p style={{ textAlign: 'center', color: '#475569', fontWeight: 500, marginBottom: '16px' }}>
                  Existing entries for this time slot
                </p>
                {context.existingItems.map((item, index) => (
                  <div key={index} style={{
                    background: 'rgba(255,255,255,0.9)', padding: '16px',
                    borderRadius: '12px', border: '1px solid rgba(59,130,246,0.15)',
                    marginBottom: '12px',
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                      <div><strong style={{ color: '#1e40af' }}>Subject:</strong> {item.subject_name}</div>
                      <div><strong style={{ color: '#1e40af' }}>Teacher:</strong> {item.teacher_name || '—'}</div>
                      <div><strong style={{ color: '#1e40af' }}>Type:</strong> {item.slot_type === 'break' ? 'Break' : 'Class'}</div>

                      <div><strong style={{ color: '#1e40af' }}>Days:</strong> {(Array.isArray(item.day_of_week) ? item.day_of_week : []).join(', ')}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className="field-section">
                <div className="section-header">
                  <div className="icon-circle"><i className="pi pi-sitemap" /></div>
                  <span>Select Timetable & Class</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <GlobalChipSelector
                    label="Timetable Type"
                    icon="pi pi-calendar"
                    value={formData.timetableTypeId}
                    onChange={(v) => setFormData({
                      ...formData,
                      timetableTypeId: v,
                      entries: [{
                        id: crypto.randomUUID(),
                        timeSlotId: '', subjectId: '',
                        teacherAssignmentId: '', daysOfWeek: [], roomNumber: '',
                      }],
                    })}
                    options={timetableTypeChipOptions}
                    required
                    disabled={loading || !!context?.timetableTypeId}
                    emptyMessage="No timetable types available"
                  />

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}>
                    <GlobalChipSelector
                      label="Class"
                      icon="pi pi-users"
                      value={formData.classId}
                      onChange={(v) => setFormData({
                        ...formData,
                        classId: v,
                        section: null,
                        entries: [{
                          id: crypto.randomUUID(),
                          timeSlotId: '', subjectId: '',
                          teacherAssignmentId: '', daysOfWeek: [], roomNumber: '',
                        }],
                      })}
                      options={classChipOptions}
                      required
                      loading={isFetching}
                      disabled={loading || !!context?.classId}
                      emptyMessage="No classes available"
                    />

                    {hasSections && (
                      <GlobalChipSelector
                        label="Section"
                        icon="pi pi-list"
                        value={formData.section || ''}
                        onChange={(v) => setFormData({ ...formData, section: v })}
                        options={sectionChipOptions}
                        required
                        disabled={loading || !!context?.section}
                      />
                    )}
                  </div>

                  {formData.classId && !hasSections && (
                    <div className="info-note">
                      <i className="pi pi-info-circle" />
                      This class has no sections — entry will apply to the whole class
                    </div>
                  )}
                </div>
              </div>

              {canShowEntries && (
                <div className="field-section">
                  <div className="section-header">
                    <div className="icon-circle"><i className="pi pi-list" /></div>
                    <span>Period Entries ({formData.entries.length})</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {formData.entries.map((entry, index) => {
                      const occupiedDays = getOccupiedDaysForSlot(entry.id, entry.timeSlotId);
                      const entrySlotType = timeSlots.find((s) => s.id === entry.timeSlotId)?.slotType ?? null;
                      return (
                        <TimetableEntryCard
                          key={entry.id}
                          entry={entry}
                          index={index}
                          classId={formData.classId}
                          section={formData.section}
                          canRemove={formData.entries.length > 1}
                          timeSlotOptions={timeSlotChipOptions}
                          subjectOptions={subjectChipOptions}
                          timeSlotsLoading={timeSlotsLoading}
                          subjectsLoading={subjectsLoading}
                          timeSlotsError={timeSlotsError}
                          subjectsError={subjectsError}
                          canShowEntries={canShowEntries}
                          loading={loading}
                          occupiedDays={occupiedDays}
                          slotType={entrySlotType}
                          onUpdate={handleUpdateEntry}
                          onUpdateDays={handleUpdateDays}
                          onRemove={handleRemoveEntry}
                        />
                      );
                    })}

                    <button
                      className="add-entry-button"
                      onClick={handleAddEntry}
                      disabled={loading || subjectsLoading || timeSlotsLoading}
                    >
                      <i className="pi pi-plus-circle" />
                      Add Another Entry
                    </button>
                  </div>
                </div>
              )}

              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <GlobalButton
                  label={
                    loading
                      ? 'Adding Entries...'
                      : `Add ${formData.entries.length} ${formData.entries.length === 1 ? 'Entry' : 'Entries'}`
                  }
                  onClick={handleSubmit}
                  variant="primary"
                  size="medium"
                  disabled={loading || isFetching || timeSlotsLoading || subjectsLoading}
                  loading={loading}
                />
              </div>
            </div>
          </div>

          {/* ═════════ RIGHT: assigned subjects sidebar ═════════ */}
          {sidebarOpen && renderSidebar()}
        </div>
      </Dialog>
    </>
  );
};

export default AddTimetableEntryModal;